import React, { JSX, useCallback, useMemo } from "react";
import { FlexOrderCarouselProps } from "../FlexOrderCarousel/FlexOrderCarousel";
import { Box, BoxProps, Typography } from "@mui/material";
import { colors } from "../../styles/theme";
import {
  useActiveCategory,
  useGroupBuckets,
  useMainCategoryStaticData,
  useSelectedGroup,
} from "../../store/selectors";
import { groupByRelation } from "../../utils/groupByRelation";
import { GroupBucket, UID } from "@shared/core";
import { useConfigStore } from "../../store";
import { useResponsiveScaler } from "../../lib/ResponsiveScaler";
import { FlexOrder } from "../FlexOrder/FlexOrder";
import { useDeviceStyles } from "src/hooks/useDeviceStyles";

export type GroupedCategoriesProps = {
  getIndexForGroup: (groupId: string) => number;
  orderCarouseProps?: FlexOrderCarouselProps;
} & BoxProps;

export const GroupedCategories: React.FC<GroupedCategoriesProps> = (
  props,
): JSX.Element => {
  const { getIndexForGroup, orderCarouseProps, ...rest } = props;
  const { sx, ...restWithNoSx } = rest;

  const activeCategory = useActiveCategory();
  const mainCategoryStaticData = useMainCategoryStaticData();
  const groupBuckets = useGroupBuckets(activeCategory);
  const selectedGroup = useSelectedGroup(activeCategory);

  const setSelectedGroup = useConfigStore((s) => s.setSelectedGroup);
  const selectSingle = useConfigStore((s) => s.selectSingle);
  const { isMobileDevice } = useDeviceStyles();

  const { rpx } = useResponsiveScaler();

  const catalogDataGrouped = useMemo(() => {
    return groupByRelation(mainCategoryStaticData.items);
  }, [mainCategoryStaticData]);

  const ordersGrouped = useMemo(() => {
    return catalogDataGrouped.map((_, i) => i);
  }, [catalogDataGrouped]);

  const handleGroupChange = useCallback(
    (bucket: GroupBucket) => {
      if (!bucket) return;

      const groupUid = bucket.relation.uid;

      setSelectedGroup(activeCategory, groupUid as any);

      const idx = getIndexForGroup(groupUid);
      const item = bucket.items[idx] ?? bucket.items[0];

      if (item) {
        selectSingle(activeCategory, item.uid as UID);
      }
    },
    [activeCategory, getIndexForGroup, selectSingle, setSelectedGroup],
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        whiteSpace: "nowrap",
        ...rpx({
          padding: "15px 0",
        }),
        ...sx,
      }}
      {...restWithNoSx}
    >
      <FlexOrder
        key={`${activeCategory}-${groupBuckets.length}`}
        orders={ordersGrouped}
        itemClassName="item"
        {...orderCarouseProps}
        sx={{
          display: "flex",

          ...(orderCarouseProps?.sx || {}),
        }}
      >
        {groupBuckets.map((el) => {
          const isActive = selectedGroup === el.relation.uid;

          return (
            <Box
              key={el.relation.uid}
              onClick={() => handleGroupChange(el)}
              sx={{
                width: 200, // ✅ fixed width instead of minWidth
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                userSelect: "none",
                cursor: isActive ? "default" : "pointer",
              }}
            >
              <Typography
                variant={"v2_special"}
                sx={{
                  fontSize: "18px",
                  letterSpacing: "0.05em",
                  textAlign: "center",
                  color: isActive ? colors.text.v4 : colors.text.v2,
                  whiteSpace: "nowrap",
                }}
              >
                {isMobileDevice && el.relation.name === "Natural certified Teak"
                  ? "Natural"
                  : el.relation.name}
              </Typography>
            </Box>
          );
        })}
      </FlexOrder>
    </Box>
  );
};
